import useActiveGym from "@ui/hooks/active-gym";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import * as analytics from "@/lib/analytics";
import type { FeatureAnnouncementEntry } from "./registry";
import { announcementShownThisSession, markAnnouncementShown } from "./store";

/**
 * Lifecycle shared by every announcement renderer: the once-per-session
 * "Shown" event (which doubles as the entrance-animation gate, so paging back
 * and forth is not re-counted or re-animated), the primary action (track,
 * persist the dismissal via `onDismiss`, then navigate: in-app for internal
 * hrefs, a new tab otherwise), and the plain dismissal. Every event carries
 * the announcement id, its display, and the gym name for per-account funnels.
 */
export function useAnnouncementLifecycle(
  announcement: FeatureAnnouncementEntry,
  onDismiss: () => void,
) {
  const router = useRouter();
  const { gym } = useActiveGym();
  const gymName = gym?.name;

  const freshRef = useRef<boolean | null>(null);
  if (freshRef.current === null) {
    freshRef.current = !announcementShownThisSession(announcement.id);
  }

  useEffect(() => {
    if (!freshRef.current) return;
    markAnnouncementShown(announcement.id);
    analytics.track("Feature Announcement Shown", {
      announcement: announcement.id,
      display: announcement.display,
      placement: announcement.placement,
      gym: gymName,
    });
  }, [announcement.id, announcement.display, announcement.placement, gymName]);

  function complete() {
    analytics.track("Feature Announcement Action Clicked", {
      announcement: announcement.id,
      display: announcement.display,
      placement: announcement.placement,
      gym: gymName,
    });
    onDismiss();
    const href = announcement.primaryAction.href;
    if (href.startsWith("/")) {
      router.push(href);
    } else {
      window.open(href, "_blank", "noopener,noreferrer");
    }
  }

  function dismiss() {
    analytics.track("Feature Announcement Dismissed", {
      announcement: announcement.id,
      display: announcement.display,
      placement: announcement.placement,
      gym: gymName,
    });
    onDismiss();
  }

  return { fresh: freshRef.current, complete, dismiss };
}
